import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Divider,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import { useNavigate } from "react-router-dom";
import { useOptionalAuth } from "../../auth/AuthContext";
import { canActForOwners, enterOwnerShell } from "../../auth/actingOwner";
import {
  normalisePortalHost,
  PORTAL_DEFAULTS,
  playShowsAll,
  portalShowsSeries,
  type PlayContent,
  type PortalHostError,
  type PortalHostStatus,
  type PortalSettings,
} from "@shared/consumer-tvod";
import EditorCard from "../template/EditorCard";
import { fieldGrid } from "../template/editorLayout";
import { tokens } from "../../theme";
import { EditDrawer } from "../EditDrawer";
import { friendlyError } from "../../utils/apiError";
import { useFormat } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";
import { PermissionGate } from "../../auth/access";
import { describeError, useToast } from "../toast";
import { foyerApi } from "../../http/foyer";
import { portalApi, type PortalView } from "../../http/portal";
import PortalAppearancePanel from "./PortalAppearancePanel";
import ProviderRevenuePanel from "../revenue/ProviderRevenuePanel";

/**
 * A content provider's purchase portal (consumer TVOD,
 * `docs/design/consumer-tvod.md` §3, §8): its domain, the key the vendor's
 * app build carries, whether it sells, what it carries, its settings - and,
 * on the next tabs, how it looks (logo, colours, hero, type) and its
 * revenue. Offers are not here: the provider sets its own offer matrix in
 * its supplier pages (ORG-1, Sigge 2026-10-06).
 *
 * The host has its own Save because saving it does something outside the
 * form: the server registers it on our Cloudflare zone and checks DNS
 * straight away. Everything else is one draft with one Save.
 */

export interface IdName {
  id: string;
  name: string;
}

/** What the status chip says, and in which colour. Derived, never stored. */
export function portalHostStatusView(
  t: Translate,
  status: PortalHostStatus | null
): {
  label: string;
  fg: string;
  bg: string;
} {
  const label = (key: PortalHostStatus | "none") =>
    t(`offers:purchasePortalDialog.hostStatus.${key}`);
  switch (status) {
    case "active":
      return {
        label: label("active"),
        fg: tokens.success,
        bg: tokens.successTint,
      };
    case "pending_certificate":
      return {
        label: label("pending_certificate"),
        fg: tokens.warning,
        bg: tokens.warningTint,
      };
    case "pending_dns":
      return {
        label: label("pending_dns"),
        fg: tokens.warning,
        bg: tokens.warningTint,
      };
    case "failed":
      return { label: label("failed"), fg: tokens.error, bg: tokens.errorTint };
    default:
      return { label: label("none"), fg: tokens.inkMuted, bg: tokens.rowAlt };
  }
}

const HOST_ERRORS: readonly PortalHostError[] = [
  "empty",
  "invalid",
  "ip_address",
  "single_label",
  "too_long",
  "reserved",
  "nenda_domain",
];

export function hostErrorCopy(
  t: Translate,
  reason: string | undefined
): string {
  const key = HOST_ERRORS.includes(reason as PortalHostError)
    ? reason
    : "unknown";
  return translateKey(t, `offers:purchasePortalDialog.hostErrors.${key}`);
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const { t } = useTranslation("offers");
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={done ? t("purchasePortalDialog.copied") : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={() =>
          void copy(text).then((ok) => {
            setDone(ok);
            if (ok) setTimeout(() => setDone(false), 1500);
          })
        }
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

/** A provider (feed) the portal may carry, with the company it belongs to. */
interface ProviderOption {
  id: string;
  name: string;
  /** Belongs to a company: listed first, above the divider. */
  hasCompany: boolean;
}

export default function PurchasePortalDialog({
  ownerId,
  ownerName,
  onClose,
}: {
  ownerId: string;
  ownerName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const [tab, setTab] = useState<"portal" | "appearance" | "revenue">("portal");
  const navigate = useNavigate();
  // A Nenda admin can work inside this owner's own shell, to support it as a
  // SaaS customer (Sigge, 2026-10-14) - titles, customers, plans, team.
  const canOpenShell = canActForOwners(useOptionalAuth()?.user);
  const [portal, setPortal] = useState<PortalView | null>(null);
  // Bumped on every server answer so the draft below re-seeds from it
  // (seed once, remount by key - [[cms-list-and-editor]]).
  const [version, setVersion] = useState(0);
  // Every provider (feed) in the catalogue (Sigge, 2026-10-19) - minus this
  // company's own feeds, which are always carried. One flat list: providers
  // with a company first, a divider, then the catalogue-only ones; each
  // half by name. A feed whose name differs from its company's says whose
  // it is ("Label 2 (Tigrar)").
  const [owners, setOwners] = useState<ProviderOption[]>([]);
  const [packages, setPackages] = useState<IdName[]>([]);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback((view: PortalView) => {
    setPortal(view);
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    let alive = true;
    portalApi.get(ownerId).then(
      (view) => alive && accept(view),
      (e) => alive && setError(friendlyError(t, e))
    );
    foyerApi.listContentSuppliers().then(
      (res) =>
        alive &&
        setOwners(
          res.suppliers
            .filter((f) => f.company?.id !== ownerId)
            .map((f) => ({
              id: f.id,
              name:
                f.company && f.company.name !== f.name
                  ? `${f.name} (${f.company.name})`
                  : f.name,
              hasCompany: Boolean(f.company),
            }))
            .sort(
              (a, b) =>
                Number(b.hasCompany) - Number(a.hasCompany) ||
                a.name.localeCompare(b.name, "sv")
            )
        ),
      () => alive && setOwners([])
    );
    foyerApi.listContentPackages().then(
      (res) =>
        alive &&
        setPackages(res.packages.map((p) => ({ id: p.id, name: p.name }))),
      () => alive && setPackages([])
    );
    return () => {
      alive = false;
    };
  }, [ownerId, accept, t]);

  return (
    <EditDrawer
      open
      onClose={onClose}
      title={t("purchasePortalDialog.title", { name: ownerName })}
      // The appearance tab carries a live preview beside the form.
      width={tab === "appearance" ? 1200 : 900}
      bodyBg={tokens.rowAlt}
      data-testid="purchase-portal-drawer"
      tabs={
        <Tabs
          value={tab}
          onChange={(_, v: "portal" | "appearance" | "revenue") => setTab(v)}
        >
          <Tab
            value="portal"
            label={t("purchasePortalDialog.tabs.portal")}
            data-testid="portal-tab-portal"
          />
          <Tab
            value="appearance"
            label={t("purchasePortalDialog.tabs.appearance")}
            data-testid="portal-tab-appearance"
          />
          <Tab
            value="revenue"
            label={t("purchasePortalDialog.tabs.revenue")}
            data-testid="portal-tab-revenue"
          />
        </Tabs>
      }
      actions={(dismiss) => (
        <>
          {/* The staff register, filtered to this portal (audited there). */}
          <Button
            href={`/distribution/portal-customers?portal=${encodeURIComponent(ownerId)}`}
            startIcon={<PeopleIcon />}
            sx={{ mr: "auto" }}
            data-testid="portal-customers-link"
          >
            {t("purchasePortalDialog.customers")}
          </Button>
          {canOpenShell ? (
            <Button
              startIcon={<StorefrontIcon />}
              onClick={() => {
                enterOwnerShell(ownerId);
                navigate("/supplier/titles");
              }}
              data-testid="portal-open-shell"
            >
              {t("purchasePortalDialog.openShell", { name: ownerName })}
            </Button>
          ) : null}
          <Button onClick={() => dismiss()}>{t("common:buttons.close")}</Button>
        </>
      )}
    >
      {tab === "revenue" ? (
        // The provider's statement: about its TITLES wherever they sold,
        // so it does not wait for the portal settings to load.
        <ProviderRevenuePanel ownerId={ownerId} ownerName={ownerName} />
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : !portal ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : tab === "portal" ? (
        <PortalForm
          key={version}
          portal={portal}
          owners={owners}
          packages={packages}
          onSaved={accept}
        />
      ) : (
        // Not keyed by `version`: its own save must not remount it (and
        // drop its "Saved."); switching tabs remounts it anyway.
        <PortalAppearancePanel portal={portal} onSaved={accept} />
      )}
    </EditDrawer>
  );
}

/* -------------------------------------------------------------------------- */

interface Draft {
  key: string;
  enabled: boolean;
  /** "Visa serier"; on = absent from the stored settings. */
  showSeries: boolean;
  /** What signed-in members see; `all` = absent from the stored settings. */
  playContent: PlayContent;
  carriedFeedIds: string[];
  packageIds: string[];
  settings: Record<keyof PortalSettings, string>;
}

const SETTING_KEYS: Array<keyof PortalSettings> = [
  "defaultLocale",
  "supportEmail",
  "termsUrl",
  "privacyUrl",
  "heartbeatSeconds",
  "startWindowDays",
  "vatBps",
];

function seed(portal: PortalView): Draft {
  const settings = {} as Record<keyof PortalSettings, string>;
  for (const k of SETTING_KEYS) {
    const v = portal.settings[k];
    settings[k] =
      v === undefined
        ? ""
        : k === "vatBps"
          ? String((v as number) / 100)
          : String(v);
  }
  return {
    key: portal.key ?? "",
    enabled: portal.enabled,
    showSeries: portalShowsSeries(portal.settings),
    playContent: playShowsAll(portal.settings) ? "all" : "purchased",
    carriedFeedIds: portal.carriedFeedIds,
    packageIds: portal.packageIds,
    settings,
  };
}

/** The draft's settings as the API takes them; "" = platform default. */
function settingsOut(draft: Draft): PortalSettings {
  const s = draft.settings;
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  const vat = num(s.vatBps);
  return {
    ...(s.defaultLocale
      ? { defaultLocale: s.defaultLocale as "sv" | "en" }
      : {}),
    ...(s.supportEmail.trim() ? { supportEmail: s.supportEmail.trim() } : {}),
    ...(s.termsUrl.trim() ? { termsUrl: s.termsUrl.trim() } : {}),
    ...(s.privacyUrl.trim() ? { privacyUrl: s.privacyUrl.trim() } : {}),
    ...(num(s.heartbeatSeconds) !== undefined
      ? { heartbeatSeconds: num(s.heartbeatSeconds) }
      : {}),
    ...(num(s.startWindowDays) !== undefined
      ? { startWindowDays: num(s.startWindowDays) }
      : {}),
    ...(vat !== undefined ? { vatBps: Math.round(vat * 100) } : {}),
    ...(draft.showSeries ? {} : { showSeries: false }),
    ...(draft.playContent === "purchased"
      ? { playContent: "purchased" as const }
      : {}),
  };
}

/** The refused settings fields with a hint under `settingsHints`. */
const SETTINGS_WITH_HINT = SETTING_KEYS.map((k) => `settings.${k}`);

function PortalForm({
  portal,
  owners,
  packages,
  onSaved,
}: {
  portal: PortalView;
  owners: ProviderOption[];
  packages: IdName[];
  onSaved: (view: PortalView) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const [hostInput, setHostInput] = useState(portal.host ?? "");
  const [draft, setDraft] = useState<Draft>(() => seed(portal));
  const [busy, setBusy] = useState<null | "host" | "check" | "save">(null);
  const [hostError, setHostError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{
    field: string;
    hint: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = hostInput.trim()
    ? normalisePortalHost(hostInput, {
        allowNendaHosts: portal.allowNendaHosts ?? [],
      })
    : null;
  const hostDirty =
    (preview?.ok ? preview.host : hostInput.trim()) !== (portal.host ?? "");
  const status = portalHostStatusView(t, portal.hostStatus);

  const setSetting = (k: keyof PortalSettings, v: string) =>
    setDraft((d) => ({ ...d, settings: { ...d.settings, [k]: v } }));

  const saveHost = async (host: string | null) => {
    setBusy("host");
    setHostError(null);
    try {
      onSaved(await portalApi.update(portal.ownerId, { host }));
      toast.success(
        host
          ? t("purchasePortalDialog.hostSaved", {
              host,
              name: portal.ownerName,
            })
          : t("purchasePortalDialog.hostRemoved", { name: portal.ownerName })
      );
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { hint?: string; reason?: string };
      };
      setHostError(
        err.code === "already_taken"
          ? (err.body?.hint ?? t("purchasePortalDialog.hostTaken"))
          : err.code === "invalid"
            ? hostErrorCopy(t, err.body?.reason)
            : friendlyError(t, e)
      );
      toast.error(
        describeError(t, "savePortalHost", e, { name: portal.ownerName })
      );
      setBusy(null);
    }
  };

  const checkNow = async () => {
    setBusy("check");
    setHostError(null);
    try {
      onSaved(await portalApi.checkNow(portal.ownerId));
      toast.success(
        t("purchasePortalDialog.dnsCheckDone", {
          host: portal.host ?? portal.ownerName,
        })
      );
    } catch (e) {
      setHostError(friendlyError(t, e));
      toast.error(
        describeError(t, "checkPortalDns", e, { name: portal.ownerName })
      );
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    setError(null);
    setFieldError(null);
    try {
      onSaved(
        await portalApi.update(portal.ownerId, {
          ...(draft.key.trim() && draft.key.trim() !== portal.key
            ? { key: draft.key.trim() }
            : {}),
          enabled: draft.enabled,
          carriedFeedIds: draft.carriedFeedIds,
          packageIds: draft.packageIds,
          settings: settingsOut(draft),
        })
      );
      toast.success(
        t("purchasePortalDialog.portalSaved", { name: portal.ownerName })
      );
    } catch (e) {
      const err = e as {
        code?: string;
        body?: { field?: string; hint?: string; ids?: string[] };
      };
      const field = err.body?.field;
      if (
        field &&
        (err.code === "invalid" ||
          err.code === "already_taken" ||
          err.code === "key_locked")
      ) {
        setFieldError({
          field,
          hint:
            err.body?.hint ??
            (SETTINGS_WITH_HINT.includes(field)
              ? translateKey(
                  t,
                  `offers:purchasePortalDialog.settingsHints.${field.slice("settings.".length)}`
                )
              : err.body?.ids?.length
                ? t("purchasePortalDialog.settingsHints.notFound", {
                    ids: err.body.ids.join(", "),
                  })
                : t("purchasePortalDialog.settingsHints.unknown")),
        });
      } else {
        setError(friendlyError(t, e));
      }
      toast.error(
        field
          ? t("purchasePortalDialog.checkField", {
              name: portal.ownerName,
              field,
            })
          : describeError(t, "savePortal", e, { name: portal.ownerName })
      );
      setBusy(null);
    }
  };

  const fieldHint = (field: string, fallback: string) =>
    fieldError?.field === field ? fieldError.hint : fallback;

  return (
    <Stack spacing={2.5} sx={{ py: 2 }}>
      {/* ── Domain ─────────────────────────────────────────────────── */}
      <EditorCard
        eyebrow={t("purchasePortalDialog.domainEyebrow")}
        data-testid="portal-domain-card"
        action={
          <Box
            data-testid="portal-host-status"
            sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 99,
              typography: "meta",
              fontWeight: "fontWeightSemibold",
              color: status.fg,
              bgcolor: status.bg,
            }}
          >
            {status.label}
          </Box>
        }
        hint={t("purchasePortalDialog.domainHint")}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label={t("purchasePortalDialog.hostName")}
              size="small"
              fullWidth
              value={hostInput}
              onChange={(e) => {
                setHostInput(e.target.value);
                setHostError(null);
              }}
              placeholder="film.studio.se"
              error={!!hostError || (preview !== null && !preview.ok)}
              helperText={
                hostError ??
                (preview && !preview.ok
                  ? hostErrorCopy(t, preview.error)
                  : preview?.ok && preview.host !== hostInput.trim()
                    ? t("purchasePortalDialog.savedAs", { host: preview.host })
                    : t("purchasePortalDialog.hostHelp"))
              }
              inputProps={{ "data-testid": "portal-host-input" }}
            />
            <PermissionGate
              resource="content-owners"
              level="administrate"
              place="nenda"
              deniedProps={{ disabled: true }}
            >
              <Button
                variant="contained"
                disabled={
                  busy !== null || !hostDirty || !preview || !preview.ok
                }
                onClick={() => preview?.ok && void saveHost(preview.host)}
                data-testid="portal-host-save"
                sx={{ flexShrink: 0, height: 40 }}
              >
                {busy === "host"
                  ? t("common:states.saving")
                  : t("purchasePortalDialog.saveHost")}
              </Button>
            </PermissionGate>
          </Stack>

          {portal.host ? (
            <>
              {portal.hostError ? (
                <Alert
                  severity={portal.hostStatus === "failed" ? "error" : "info"}
                  data-testid="portal-host-error"
                >
                  {portal.hostError}
                </Alert>
              ) : null}

              <Box>
                <Typography
                  variant="meta"
                  sx={{ color: tokens.inkMuted, mb: 1 }}
                >
                  {t("purchasePortalDialog.dnsRecords")}
                </Typography>
                <Stack spacing={1} data-testid="portal-dns-records">
                  {portal.dnsRecords.map((r) => {
                    const line = `${r.name}  ${r.type}  ${r.value}`;
                    return (
                      <Stack
                        key={`${r.type}-${r.name}`}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          border: `1px solid ${tokens.divider}`,
                          borderRadius: "8px",
                          bgcolor: tokens.surface,
                        }}
                      >
                        <Typography
                          variant="bodySm"
                          sx={{
                            fontFamily: "fontFamilyMono",
                            flex: 1,
                            wordBreak: "break-all",
                          }}
                          data-testid="portal-dns-record"
                        >
                          {line}
                        </Typography>
                        <CopyButton
                          text={line}
                          label={t("purchasePortalDialog.copyRecord")}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
                <Typography
                  variant="micro"
                  sx={{ color: tokens.inkFaint, mt: 0.75 }}
                >
                  {t("purchasePortalDialog.bareDomainNote")}
                </Typography>
              </Box>

              {portal.certificateProvider === "fake" ? (
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("purchasePortalDialog.noCloudflare")}
                </Typography>
              ) : null}

              <Stack direction="row" spacing={1} alignItems="center">
                <PermissionGate
                  resource="content-owners"
                  level="administrate"
                  place="nenda"
                >
                  <Button
                    variant="outlined"
                    onClick={() => void checkNow()}
                    disabled={busy !== null}
                    data-testid="portal-check-now"
                  >
                    {busy === "check"
                      ? t("purchasePortalDialog.checking")
                      : t("purchasePortalDialog.checkNow")}
                  </Button>
                  <Button
                    color="error"
                    onClick={() => void saveHost(null)}
                    disabled={busy !== null}
                    data-testid="portal-host-remove"
                  >
                    {t("purchasePortalDialog.removeHost")}
                  </Button>
                </PermissionGate>
                <Box sx={{ flex: 1 }} />
                <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                  {portal.hostCheckedAt
                    ? t("purchasePortalDialog.lastChecked", {
                        when: fmt.dateTime(portal.hostCheckedAt),
                      })
                    : t("purchasePortalDialog.notChecked")}
                </Typography>
              </Stack>
            </>
          ) : null}
        </Stack>
      </EditorCard>

      {/* ── App build ──────────────────────────────────────────────── */}
      <EditorCard
        eyebrow={t("purchasePortalDialog.appBuildEyebrow")}
        hint={t("purchasePortalDialog.appBuildHint")}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            label={t("purchasePortalDialog.portalKey")}
            size="small"
            fullWidth
            value={draft.key}
            onChange={(e) =>
              setDraft((d) => ({ ...d, key: e.target.value.toLowerCase() }))
            }
            disabled={portal.keyLocked}
            placeholder={t("purchasePortalDialog.keyPlaceholder")}
            error={fieldError?.field === "key"}
            helperText={fieldHint(
              "key",
              portal.keyLocked
                ? t("purchasePortalDialog.keyLocked")
                : t("purchasePortalDialog.keyHelp")
            )}
            InputProps={{ sx: { fontFamily: "fontFamilyMono" } }}
            inputProps={{ "data-testid": "portal-key" }}
          />
          {portal.key ? (
            <Box sx={{ pt: 0.5 }}>
              <CopyButton
                text={portal.key}
                label={t("purchasePortalDialog.copyKey")}
              />
            </Box>
          ) : null}
        </Stack>
      </EditorCard>

      {/* ── Availability ───────────────────────────────────────────── */}
      <EditorCard eyebrow={t("purchasePortalDialog.availabilityEyebrow")}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            checked={draft.enabled}
            onChange={(e) =>
              setDraft((d) => ({ ...d, enabled: e.target.checked }))
            }
            inputProps={
              {
                "aria-label": t("purchasePortalDialog.portalEnabled"),
                "data-testid": "portal-enabled",
              } as never
            }
          />
          <Typography variant="body2">
            {draft.enabled
              ? t("purchasePortalDialog.enabledOn")
              : t("purchasePortalDialog.enabledOff")}
          </Typography>
        </Stack>
      </EditorCard>

      {/* ── Catalogue ──────────────────────────────────────────────── */}
      <EditorCard
        eyebrow={t("purchasePortalDialog.catalogueEyebrow")}
        hint={t("purchasePortalDialog.catalogueHint")}
      >
        <Box sx={fieldGrid(280)}>
          <Autocomplete
            multiple
            size="small"
            options={owners}
            groupBy={(o) =>
              (o as ProviderOption).hasCompany ? "company" : "none"
            }
            // No headings: a divider between the two halves is enough.
            renderGroup={(params) => (
              <li key={params.key}>
                {params.group === "none" && owners.some((o) => o.hasCompany) ? (
                  <Divider sx={{ my: 0.5 }} />
                ) : null}
                <Box component="ul" sx={{ p: 0 }}>
                  {params.children}
                </Box>
              </li>
            )}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={draft.carriedFeedIds.map(
              (id) =>
                owners.find((o) => o.id === id) ?? {
                  id,
                  name: id,
                  hasCompany: false,
                }
            )}
            onChange={(_, v) =>
              setDraft((d) => ({ ...d, carriedFeedIds: v.map((o) => o.id) }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("purchasePortalDialog.otherProviders")}
                error={fieldError?.field === "carriedFeedIds"}
                helperText={fieldHint(
                  "carriedFeedIds",
                  t("purchasePortalDialog.otherProvidersHelp")
                )}
              />
            )}
            data-testid="portal-carried-owners"
          />
          <Autocomplete
            multiple
            size="small"
            options={packages}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={draft.packageIds.map(
              (id) => packages.find((p) => p.id === id) ?? { id, name: id }
            )}
            onChange={(_, v) =>
              setDraft((d) => ({ ...d, packageIds: v.map((p) => p.id) }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("purchasePortalDialog.packages")}
                error={fieldError?.field === "packageIds"}
                helperText={fieldHint(
                  "packageIds",
                  t("purchasePortalDialog.packagesHelp")
                )}
              />
            )}
            data-testid="portal-packages"
          />
        </Box>
      </EditorCard>

      {/* ── Settings ───────────────────────────────────────────────── */}
      <EditorCard
        eyebrow={t("purchasePortalDialog.settingsEyebrow")}
        hint={t("purchasePortalDialog.settingsHint")}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Switch
            checked={draft.showSeries}
            onChange={(e) =>
              setDraft((d) => ({ ...d, showSeries: e.target.checked }))
            }
            inputProps={
              {
                "aria-label": t("purchasePortalDialog.showSeries"),
                "data-testid": "portal-show-series",
              } as never
            }
          />
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: "fontWeightSemibold" }}
            >
              {t("purchasePortalDialog.showSeries")}
            </Typography>
            <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
              {draft.showSeries
                ? t("purchasePortalDialog.showSeriesOn")
                : t("purchasePortalDialog.showSeriesOff")}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "fontWeightSemibold", mb: 0.75 }}
          >
            {t("purchasePortalDialog.membersSee")}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={draft.playContent}
            onChange={(_e, v: PlayContent | null) => {
              if (v) setDraft((d) => ({ ...d, playContent: v }));
            }}
            aria-label={t("purchasePortalDialog.membersSee")}
            data-testid="portal-play-content"
          >
            <ToggleButton value="all">
              {t("purchasePortalDialog.allContent")}
            </ToggleButton>
            <ToggleButton value="purchased">
              {t("purchasePortalDialog.purchasedOnly")}
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="meta" sx={{ color: tokens.inkMuted, mt: 0.75 }}>
            {t("purchasePortalDialog.membersSeeHelp")}
          </Typography>
        </Box>
        <Box sx={fieldGrid(220)}>
          <TextField
            select
            label={t("purchasePortalDialog.defaultLanguage")}
            size="small"
            value={draft.settings.defaultLocale}
            onChange={(e) => setSetting("defaultLocale", e.target.value)}
            helperText={fieldHint(
              "settings.defaultLocale",
              t("purchasePortalDialog.defaultIs", {
                language:
                  PORTAL_DEFAULTS.defaultLocale === "sv"
                    ? t("portalAppearancePanel.swedish")
                    : t("portalAppearancePanel.english"),
              })
            )}
          >
            <MenuItem value="">
              {t("purchasePortalDialog.platformDefault")}
            </MenuItem>
            <MenuItem value="sv">{t("portalAppearancePanel.swedish")}</MenuItem>
            <MenuItem value="en">{t("portalAppearancePanel.english")}</MenuItem>
          </TextField>
          <TextField
            label={t("purchasePortalDialog.supportEmail")}
            size="small"
            value={draft.settings.supportEmail}
            onChange={(e) => setSetting("supportEmail", e.target.value)}
            error={fieldError?.field === "settings.supportEmail"}
            helperText={fieldHint("settings.supportEmail", " ")}
            inputProps={{ "data-testid": "portal-support-email" }}
          />
          <TextField
            label={t("purchasePortalDialog.termsUrl")}
            size="small"
            value={draft.settings.termsUrl}
            onChange={(e) => setSetting("termsUrl", e.target.value)}
            placeholder="https://"
            error={fieldError?.field === "settings.termsUrl"}
            helperText={fieldHint("settings.termsUrl", " ")}
          />
          <TextField
            label={t("purchasePortalDialog.privacyUrl")}
            size="small"
            value={draft.settings.privacyUrl}
            onChange={(e) => setSetting("privacyUrl", e.target.value)}
            placeholder="https://"
            error={fieldError?.field === "settings.privacyUrl"}
            helperText={fieldHint("settings.privacyUrl", " ")}
          />
          <TextField
            label={t("purchasePortalDialog.heartbeat")}
            size="small"
            type="number"
            value={draft.settings.heartbeatSeconds}
            onChange={(e) => setSetting("heartbeatSeconds", e.target.value)}
            placeholder={String(PORTAL_DEFAULTS.heartbeatSeconds)}
            error={fieldError?.field === "settings.heartbeatSeconds"}
            helperText={fieldHint(
              "settings.heartbeatSeconds",
              t("purchasePortalDialog.heartbeatHelp")
            )}
          />
          <TextField
            label={t("purchasePortalDialog.startWindow")}
            size="small"
            type="number"
            value={draft.settings.startWindowDays}
            onChange={(e) => setSetting("startWindowDays", e.target.value)}
            placeholder={String(PORTAL_DEFAULTS.startWindowDays)}
            error={fieldError?.field === "settings.startWindowDays"}
            helperText={fieldHint(
              "settings.startWindowDays",
              t("purchasePortalDialog.defaultForNewOffer")
            )}
          />
          <TextField
            label={t("purchasePortalDialog.vat")}
            size="small"
            type="number"
            value={draft.settings.vatBps}
            onChange={(e) => setSetting("vatBps", e.target.value)}
            placeholder={String(PORTAL_DEFAULTS.vatBps / 100)}
            error={fieldError?.field === "settings.vatBps"}
            helperText={fieldHint(
              "settings.vatBps",
              t("purchasePortalDialog.defaultForNewOffer")
            )}
          />
        </Box>
      </EditorCard>

      {error ? <Alert severity="error">{error}</Alert> : null}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
      >
        <PermissionGate
          resource="content-owners"
          level="administrate"
          place="nenda"
          deniedProps={{ disabled: true }}
        >
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={busy !== null}
            data-testid="portal-save"
          >
            {busy === "save"
              ? t("common:states.saving")
              : t("purchasePortalDialog.savePortal")}
          </Button>
        </PermissionGate>
      </Stack>
    </Stack>
  );
}
